import { Images } from '@/assets/images';
import { DocTypeEnum, PageTypeEnum, REGEX } from '@/constant';
import { cn, dayjs, formatDate } from '@/utils';

import { isNotNilOrEmpty } from 'ramda-adjunct';
import * as Yup from 'yup';

import { CheckboxType, FormConfig } from '../define-document-write.type';

const initializeValue = {
	franchiseName: '',
	franchiseAddress: '',
	employerName: '',
	franchisePhone: '',
	temporaryBuilding: '',
	accommodationOther: '',
	accommodationPeopleNumber: '',
	interiorEquipmentRoomSize: '',
	peoplesInARoom: '',
	bedroomHeight: '',
	franchiseWriter: '',
	applicantWriter: '',
	registerDate: '',
	applicationRegisterNameStay: '',
	applicationSignatureStay: '',
	typeOfAccommodation: '주택',
	typeOfAccommodationPerson: '',
	genderDivision: '남녀구분',
	lockingDevice: '잠금장치 유',
	toilet: '숙소 내부, 잠금장치 유',
	locking: '잠금장치 유',
	typeToilet: '수세식',
	bathroomFurniture: '샤워시설',
	hotWater: '온수 사용 가능',
	heatingRoom: '있음',
	typeRoom: [] as string[],
	lightingAndVentilation: '있음',
	fireEquipment: '설치',
	checkInteriorEquipmentRoomSize: '',
	checkBedroomHeight: '',
	coldRoomType: '',
	heatedRoomType: '',
	lightingType: '',
};

type IValues = typeof initializeValue;

export const ACCOMMODATION_FACILITIES_CONFIG: FormConfig<
	IValues,
	DocTypeEnum.LMF_04
> = {
	initializeValue,
	title: '숙소 시설표(국문)',
	mapPropToValues({ refTableName, lodgingData, applicantData, franchiseData }) {
		const commonValue = {
			registerDate: formatDate(dayjs()),
		};
		if (refTableName === PageTypeEnum.LODGING) {
			const isFranchise =
				lodgingData?.providerTable === PageTypeEnum.FRANCHISE &&
				isNotNilOrEmpty(lodgingData?.providerId);
			return {
				...commonValue,
				franchiseName: isFranchise
					? lodgingData?.franchiseName
					: franchiseData?.name || '',
				franchiseAddress: isFranchise
					? lodgingData?.providerAddress
					: franchiseData?.address || '',
				employerName: isFranchise
					? lodgingData?.providerName
					: franchiseData?.employerName || '',
				franchisePhone: isFranchise
					? lodgingData?.providerPhone
					: franchiseData?.employerPhone || '',
				franchiseWriter: isFranchise
					? lodgingData?.providerName
					: franchiseData?.employerName || '',
				applicantWriter: applicantData?.fullName || '',
			};
		}
		if (
			refTableName === PageTypeEnum.APPLICANT ||
			refTableName === PageTypeEnum.FRANCHISE ||
			refTableName === PageTypeEnum.RECOMMENDER ||
			lodgingData?.providerTable === PageTypeEnum.FRANCHISE
		) {
			return {
				...commonValue,
				franchiseName: franchiseData?.name || lodgingData?.franchiseName || '',
				franchiseAddress:
					franchiseData?.address || lodgingData?.providerAddress || '',
				employerName:
					franchiseData?.employerName || lodgingData?.providerName || '',
				franchisePhone:
					franchiseData?.phone || lodgingData?.providerPhone || '',
				applicantWriter: applicantData?.fullName || '',
				franchiseWriter:
					franchiseData?.employerName || lodgingData?.providerName || '',
			};
		} else {
			return {
				...commonValue,
				franchiseName: franchiseData?.name || '',
				franchiseAddress: franchiseData?.address || '',
				employerName: franchiseData?.employerName || '',
				franchisePhone: franchiseData?.phone || '',
				applicantWriter: applicantData?.fullName || '',
				franchiseWriter: franchiseData?.employerName || '',
			};
		}
	},
	pages: [
		{
			background: {
				src: Images.formAccommodationFacilities,
				className: 'template',
				dataSource: Images.formAccommodationFacilities,
			},

			textAreas: [
				{
					name: 'franchiseName',
					maxHeight: 20,
					maxLength: 20,
					containerClassName: 'absolute top-[209px] left-[59px] w-[310px]',
					regex: REGEX.ALLOW_KR_EN,
					className: 'text-[16px]',
				},
				{
					name: 'franchiseAddress',
					maxHeight: 20,
					maxLength: 47,
					containerClassName: 'absolute top-[209px] left-[384px] w-[315px]',
					className: 'text-[16px]',
				},
				{
					name: 'employerName',
					maxHeight: 20,
					maxLength: 20,
					containerClassName: 'absolute top-[248px] left-[59px] w-[310px]',
					regex: REGEX.ALLOW_KR_EN,
					className: 'text-[16px]',
				},
				{
					name: 'franchisePhone',
					maxHeight: 20,
					maxLength: 20,
					containerClassName: 'absolute top-[250px] left-[384px] w-[315px]',
					className: 'text-[16px]',
				},
				(values, index) => ({
					name: 'temporaryBuilding',
					maxHeight: 20,
					maxLength: 11,
					containerClassName: 'absolute top-[367px] left-[339px] w-[84px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
					disabled: values?.typeOfAccommodation !== '가설 건축물',
				}),
				(values, index) => ({
					name: 'temporaryBuilding',
					maxHeight: 20,
					maxLength: 11,
					containerClassName: 'absolute top-[367px] left-[588px] w-[84px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
					disabled: values?.typeOfAccommodation !== '가설 건축물',
				}),
				(values, index) => ({
					name: 'accommodationOther',
					maxHeight: 20,
					maxLength: 14,
					containerClassName: 'absolute top-[386px] left-[268px] w-[138px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
					onKeyDown: (e) => {
						if (e.key === 'Tab' || e.key === 'Enter') {
							e.preventDefault();
						}
					},
					disabled: values?.typeOfAccommodation !== '기타',
				}),
				(values, index) => ({
					name: 'accommodationOther',
					maxHeight: 20,
					maxLength: 14,
					containerClassName: 'absolute top-[386px] left-[472px] w-[125px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
					onKeyDown: (e) => {
						if (e.key === 'Tab' || e.key === 'Enter') {
							e.preventDefault();
						}
					},
					disabled: values?.typeOfAccommodation !== '기타',
				}),
				(values, index) => ({
					name: 'accommodationPeopleNumber',
					maxHeight: 20,
					maxLength: 5,
					containerClassName: 'absolute top-[403px] right-[358px] w-[45px]',
					regex: REGEX.ALLOW_NUMBER,
					disabled: values?.typeOfAccommodationPerson !== '1',
				}),
				(values, index) => ({
					name: 'accommodationPeopleNumber',
					maxHeight: 20,
					maxLength: 5,
					containerClassName: 'absolute top-[434px] left-[260px] w-[45px]',
					regex: REGEX.ALLOW_NUMBER,
					disabled: values?.typeOfAccommodationPerson !== '1',
				}),
				{
					name: 'interiorEquipmentRoomSize',
					maxHeight: 20,
					maxLength: 3,
					containerClassName: 'absolute top-[487px] left-[618px] w-[50px]',
					regex: REGEX.ALLOW_NUMBER,
				},
				(values, index) => ({
					name: 'peoplesInARoom',
					maxHeight: 20,
					maxLength: 3,
					containerClassName: 'absolute top-[504px] right-[321px] w-[30px]',
					regex: REGEX.ALLOW_NUMBER,
					disabled: !values?.checkInteriorEquipmentRoomSize,
				}),
				(values, index) => ({
					name: 'peoplesInARoom',
					maxHeight: 20,
					maxLength: 3,
					containerClassName: 'absolute top-[504px] right-[143px] w-[30px]',
					regex: REGEX.ALLOW_NUMBER,
					disabled: !values?.checkInteriorEquipmentRoomSize,
				}),
				// {
				// 	name: 'bedroomHeight',
				// 	maxHeight: 20,
				// 	maxLength: 7,
				// 	containerClassName: 'absolute top-[573px] left-[425px] w-[55px]',
				// 	regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				// },

				(values, index) => ({
					name: 'coldRoomType',
					maxHeight: 40,
					maxLength: 20,
					containerClassName: 'absolute bottom-[355px] left-[315px] w-[121px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
					disabled: !values?.typeRoom?.includes('(냉방) 종류'),
				}),
				(values, index) => ({
					name: 'heatedRoomType',
					maxHeight: 40,
					maxLength: 20,
					containerClassName: 'absolute bottom-[355px] left-[550px] w-[121px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
					disabled: !values?.typeRoom?.includes('(난방) 종류'),
				}),
				(values, index) => ({
					name: 'lightingType',
					maxHeight: 22,
					maxLength: 10,
					containerClassName: 'absolute bottom-[324px] left-[302px] w-[95px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
					disabled: values?.lightingAndVentilation !== '있음',
				}),
				(values, index) => ({
					name: 'lightingType',
					maxHeight: 22,
					maxLength: 10,
					containerClassName: 'absolute bottom-[324px] left-[507px] w-[160px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
					disabled: values?.lightingAndVentilation !== '있음',
				}),
				{
					name: 'franchiseWriter',
					maxHeight: 43,
					maxLength: 22,
					className: 'text-[17.33px] text-left leading-none',
					containerClassName:
						'absolute bottom-[184px] right-[251px] w-[198px] items-end',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				},
				{
					name: 'applicantWriter',
					maxHeight: 60,
					maxLength: 33,
					className: 'text-[17.33px] text-left leading-none',
					containerClassName:
						'absolute bottom-[121px] right-[228px] w-[196px] items-start',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				},
			],
			checkboxes: [
				{
					name: 'typeOfAccommodation',
					classNames: [
						'absolute top-[296px] left-[206px] w-[12px] h-[12px]',
						'absolute top-[314px] left-[206px] w-[12px] h-[12px]',
						'absolute top-[329px] left-[206px] w-[12px] h-[12px]',
						'absolute top-[350px] left-[206px] w-[12px] h-[12px]',
						'absolute top-[370px] left-[206px] w-[12px] h-[12px]',
						'absolute top-[390px] left-[206px] w-[12px] h-[12px]',
						// 'absolute top-[406px] left-[206px] w-[12px] h-[12px]',
					],
					options: [
						'주택',
						'고시원',
						'오피스텔',
						'숙박시설',
						'가설 건축물',
						'기타',
						'주거시설 내 혼숙 인원',
					],
					iconType: 'square',
					getResetKey: (value: string) => {
						return [
							'temporaryBuilding',
							'accommodationOther',
							// 'accommodationPeopleNumber',
						];
					},
				},
				{
					name: 'typeOfAccommodationPerson',
					classNames: ['absolute top-[406px] left-[206px] w-[12px] h-[12px]'],
					options: ['1'],
					iconType: 'square',
					getResetKey: (value: string) => {
						return ['accommodationPeopleNumber'];
					},
				},
				{
					name: 'genderDivision',
					classNames: [
						'absolute top-[456px] left-[311px] w-[12px] h-[12px]',
						'absolute top-[473px] left-[311px] w-[12px] h-[12px]',
					],
					options: ['남녀구분', '남녀미구분'],
					iconType: 'square',
				},
				{
					name: 'checkInteriorEquipmentRoomSize',
					classNames: ['absolute top-[492px] left-[310px] w-[12px] h-[12px]'],
					options: ['1인당 침실 크기'],
					iconType: 'square',
					getResetKey: (value: string) => {
						return ['peoplesInARoom'];
					},
				},
				// {
				// 	name: 'checkBedroomHeight',
				// 	classNames: ['absolute top-[543px] left-[318px] w-[12px] h-[12px]'],
				// 	options: ['잠금장치'],
				// 	iconType: 'square',
				// },
				{
					name: 'lockingDevice',
					classNames: [
						'absolute top-[524px] left-[311px] w-[12px] h-[12px]',
						'absolute top-[542px] left-[311px] w-[12px] h-[12px]',
					],
					options: ['잠금장치 유', '잠금장치 무'],
					iconType: 'square',
				},
				{
					name: 'toilet',
					classNames: [
						'absolute top-[560px] left-[311px] w-[12px] h-[12px]',
						'absolute top-[576px] left-[310px] w-[12px] h-[12px]',
						// 'absolute top-[592px] left-[310px] w-[12px] h-[12px]',
						// 'absolute top-[608px] left-[310px] w-[12px] h-[12px]',
					],
					options: ['숙소 내부', '숙소 외부'],
					type: CheckboxType.SINGLE,
					iconType: 'square',
				},
				{
					name: 'locking',
					classNames: [
						// 'absolute top-[560px] left-[311px] w-[12px] h-[12px]',
						// 'absolute top-[576px] left-[310px] w-[12px] h-[12px]',
						'absolute top-[592px] left-[310px] w-[12px] h-[12px]',
						'absolute top-[608px] left-[310px] w-[12px] h-[12px]',
					],
					options: ['잠금장치 유', '잠금장치 무'],
					type: CheckboxType.SINGLE,
					iconType: 'square',
				},
				{
					name: 'typeToilet',
					classNames: [
						'absolute top-[631px] left-[311px] w-[12px] h-[12px]',
						'absolute top-[631px] right-[287px] w-[12px] h-[12px]',
					],
					options: ['수세식', '재래식'],
					iconType: 'square',
				},
				{
					name: 'bathroomFurniture',
					classNames: [
						'absolute top-[653px] left-[311px] w-[12px] h-[12px]',
						'absolute top-[669px] left-[311px] w-[12px] h-[12px]',
						'absolute top-[669px] right-[271px] w-[12px] h-[12px]',
					],
					options: ['입식세면대', '샤워시설', '욕조'],
					iconType: 'square',
				},
				{
					name: 'hotWater',
					classNames: [
						'absolute top-[687px] left-[311px] w-[12px] h-[12px]',
						'absolute top-[705px] left-[311px] w-[12px] h-[12px]',
					],
					options: ['온수 사용 가능', '온수 사용 불가'],
					iconType: 'square',
				},
				{
					name: 'heatingRoom',
					classNames: [
						'absolute bottom-[404px] left-[206px] w-[12px] h-[12px]',
						'absolute bottom-[404px] right-[344px] w-[12px] h-[12px]',
					],
					options: ['있음', '없음'],
					iconType: 'square',
					getResetKey(currentSelectKey) {
						return ['typeRoom', 'coldRoomType', 'heatedRoomType'];
					},
				},
				(values, index) => ({
					name: 'typeRoom',
					classNames: [
						'absolute bottom-[379px] left-[206px] w-[12px] h-[12px]',
						'absolute bottom-[379px] right-[342px] w-[12px] h-[12px]',
					],
					options: ['(냉방) 종류', '(난방) 종류'],
					iconType: 'square',
					disabled: values?.heatingRoom === '없음',
					getResetKey: (value: string) => {
						if (value === '(냉방) 종류') {
							return ['coldRoomType'];
						}
						if (value === '(난방) 종류') {
							return ['heatedRoomType'];
						}

						return ['heatedRoomType', 'coldRoomType'];
					},
					type: CheckboxType.MULTIPLE,
				}),
				{
					name: 'lightingAndVentilation',
					classNames: [
						'absolute bottom-[331px] left-[206px] w-[12px] h-[12px]',
						'absolute bottom-[315px] left-[206px] w-[12px] h-[12px]',
					],
					options: ['있음', '없음'],
					iconType: 'square',
					getResetKey: (value: string) => {
						return ['lightingType'];
					},
				},
				{
					name: 'fireEquipment',
					classNames: [
						'absolute bottom-[278px] left-[206px] w-[12px] h-[12px]',
						'absolute bottom-[278px] right-[367px] w-[12px] h-[12px]',
					],
					options: ['설치', ' 미설치'],
					iconType: 'square',
				},
			],
			datePickers: [
				{
					name: 'registerDate',
					segmentedInputs: true,
					containerClassName: 'absolute bottom-[244px] left-[258px] w-[223px]',
					format: 'YYYY           MM        DD',
					style: { fontSize: '18px' },
					boxStyle: { marginBottom: '-17px' },
				},
			],
			signatures: [
				{
					name: 'applicationRegisterNameStay',
					className: 'absolute bottom-[183px] right-[100px] w-[115px] h-[50px]',
				},
				{
					name: 'applicationSignatureStay',
					className: 'absolute bottom-[133px] right-[100px] w-[115px] h-[50px]',
				},
			],
			highlightBoxes: [
				{
					name: 'franchiseName',
					className: 'w-[68px] !h-[21px] top-[191px] left-[58px]',
				},
				{
					name: 'franchiseAddress',
					className: 'w-[50px] !h-[21px] top-[191px] left-[383px]',
				},
				{
					name: 'employerName',
					className: 'w-[58px] !h-[21px] top-[231px] left-[58px]',
				},
				{
					name: 'franchisePhone',
					className: 'w-[66px] !h-[21px] top-[231px] left-[383px]',
				},
				{
					names: [
						'typeOfAccommodation',
						'temporaryBuilding',
						'accommodationOther',
						'accommodationPeopleNumber',
					],
					className: 'w-[86px] top-[333px] left-[86px] !h-[45px]',
				},
				(values, index) => ({
					name: 'temporaryBuilding',
					className: cn('w-[84px] !h-[20px] top-[367px] left-[339px]', values?.typeOfAccommodation !== '가설 건축물' && 'invisible'),
				}),
				(values, index) => ({
					name: 'temporaryBuilding',
					className: cn('w-[84px] !h-[20px] top-[367px] left-[588px]', values?.typeOfAccommodation !== '가설 건축물' && 'invisible'),
				}),
				(values, index) => ({
					name: 'accommodationOther',
					className: cn('w-[138px] !h-[20px] top-[386px] left-[268px]', values?.typeOfAccommodation !== '기타' && 'invisible'),
				}),
				(values, index) => ({
					name: 'accommodationOther',
					className: cn('w-[125px] !h-[20px] top-[386px] left-[472px]', values?.typeOfAccommodation !== '기타' && 'invisible'),
				}),
				(values, index) => ({
					name: 'accommodationPeopleNumber',
					className: cn('w-[45px] !h-[20px] top-[404px] left-[391px]', values?.typeOfAccommodationPerson !== '1' && 'invisible'),
				}),
				(values, index) => ({
					name: 'accommodationPeopleNumber',
					className: cn('w-[45px] !h-[20px] top-[432px] left-[260px]', values?.typeOfAccommodationPerson !== '1' && 'invisible'),
				}),
				{
					names: [
						'genderDivision',
						'toilet',
						'typeToilet',
						'bathroomFurniture',
						'hotWater',
						'interiorEquipmentRoomSize',
						'peoplesInARoom',
						'bedroomHeight',
						'checkInteriorEquipmentRoomSize',
						'checkBedroomHeight',
						'lockingDevice',
					],
					className: 'w-[73px] top-[566px] left-[92px]',
				},
				{
					names: [
						'genderDivision',
						'checkInteriorEquipmentRoomSize',
						'lockingDevice',
						'peoplesInARoom'
					],
					requireAllNames: true,
					className: 'w-[62px] top-[487px] left-[216px] !h-[20px]',
				},
				(values, index) => ({
					name: 'peoplesInARoom',
					className: cn('w-[28px] top-[504px] right-[323px] !h-[20px]', !values?.checkInteriorEquipmentRoomSize && 'invisible'),
				}),
				(values, index) => ({
					name: 'peoplesInARoom',
					className: cn('w-[33px] top-[504px] right-[142px] !h-[20px]', !values?.checkInteriorEquipmentRoomSize && 'invisible'),
				}),
				{
					names: [
						'toilet',
						'bathroomFurniture',
						'typeToilet',
						'hotWater'
					],
					requireAllNames: true,
					className: 'w-[68px] top-[620px] left-[216px] !h-[20px]',
				},
				{
					names: ['heatingRoom', 'typeRoom'],
					className: 'w-[100px] top-[725px] left-[77px]',
				},
				(values, index) => ({
					name: 'coldRoomType',
					className: cn('w-[121px] bottom-[355px] left-[315px] !h-[40px]', !values?.typeRoom?.includes('(냉방) 종류') && 'invisible'),
				}),
				(values, index) => ({
					name: 'heatedRoomType',
					className: cn('w-[121px] bottom-[355px] left-[550px] !h-[40px]', !values?.typeRoom?.includes('(난방) 종류') && 'invisible'),
				}),
				{
					name: 'lightingAndVentilation',
					className: 'w-[142px] top-[790px] left-[54px]',
				},
				(values, index) => ({
					name: 'lightingType',
					className: cn('w-[95px] bottom-[324px] left-[302px]', values?.lightingAndVentilation !== '있음' && 'invisible'),
				}),
				(values, index) => ({
					name: 'lightingType',
					className: cn('w-[160px] bottom-[324px] left-[507px]', values?.lightingAndVentilation !== '있음' && 'invisible'),
				}),
				{
					name: 'fireEquipment',
					className: 'w-[74px] top-[842px] left-[92px]',
				},
				{
					name: 'registerDate',
					className: 'w-[226px] top-[890px] left-[247px]',
				},
				{
					name: 'franchiseWriter',
					className: 'w-[200px] top-[938px] left-[344px]',
				},
				{
					name: 'applicantWriter',
					className: 'w-[200px] top-[962px] left-[369px]',
				},
				{
					name: 'applicationRegisterNameStay',
					className: 'bottom-[184px] right-[95px] w-[134px]',
				},
				{
					name: 'applicationSignatureStay',
					className: 'bottom-[161px] right-[90px] w-[134px]',
				},
			],
		},
	],
	validationSchema: Yup.object()
		.shape({
			franchiseName: Yup.string().required(),
			franchiseAddress: Yup.string().required(),
			employerName: Yup.string().required(),
			franchisePhone: Yup.string().required(),
			lightingAndVentilation: Yup.string().required(),
			fireEquipment: Yup.string().required(),
			registerDate: Yup.string().required(),
			franchiseWriter: Yup.string().required(),
			applicantWriter: Yup.string().required(),
			applicationRegisterNameStay: Yup.string().required(),
			applicationSignatureStay: Yup.string().required(),
		})
		.test(
			'at-least-one-field',
			'At least one field must have data',
			function (value) {
				const fields = [
					[
						'typeOfAccommodation',
						'temporaryBuilding',
						'accommodationOther',
						'accommodationPeopleNumber',
					],
					[
						'genderDivision',
						'toilet',
						'typeToilet',
						'bathroomFurniture',
						'hotWater',
						'interiorEquipmentRoomSize',
						'peoplesInARoom',
						'bedroomHeight',
						'checkInteriorEquipmentRoomSize',
						'checkBedroomHeight',
						'lockingDevice',
					],
					['heatingRoom', 'typeRoom'],
				];
				return fields.every((field) =>
					// @ts-expect-error: ignore here
					field?.some((f) => isNotNilOrEmpty(value[f]))
				);
			}
		),
};
